import { useState, useMemo } from 'react'
import { Typography } from '@mui/material'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { useLocale } from 'src/lib/hooks/useLocal'
import { RupeeIcon, Milestone, MILESTONE_TYPES } from '@shopflo/ui'
import FloGenericDialog from '../common/feedback-and-dialog/GenericDialog'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { Truck, Percent, Gift } from 'react-feather'
import ValueEditor, { Value } from './HeaderRichText'
import { successToast } from 'src/lib/utils/toasters'
import { Info } from 'react-feather'

export const MILESTONE_TO_ICON_MAPPER = {
  [MILESTONE_TYPES.FLAT_OFF]: () => <RupeeIcon />,
  [MILESTONE_TYPES.FREE_SHIPPING]: () => <Truck size={16} />,
  [MILESTONE_TYPES.PERCENT_OFF]: () => <Percent size={16} />,
  [MILESTONE_TYPES.FREEBIE]: () => <Gift size={16} />
}

const useAddMilestone = () => {
  const {
    state: { draftMilestone, progressBar },
    actions: { addMilestone }
  } = useCartContext()

  const [errors, setErrors] = useState({
    milestone_amount: false,
    milestone_text: false,
    milestone_header: false,
    milestone_header_char_count: false,
    milestone_amount_same: false
  })

  const validateMilestone = () => {
    const newErrors = {
      milestone_amount: draftMilestone.milestone_amount <= 0,
      milestone_amount_same: progressBar.milestones
        .filter(milestone => milestone.milestone !== draftMilestone.milestone)
        .some(milestone => milestone.milestone_amount === draftMilestone.milestone_amount),
      milestone_text: draftMilestone.milestone_text.trim() === '',
      milestone_header:
        draftMilestone.milestone_header.trim() === '' ||
        (draftMilestone.milestone_header.match(/{{price}}/g) || []).length > 1,
      milestone_header_char_count: draftMilestone.milestone_header.length > 52
    }
    setErrors(newErrors)

    return !Object.values(newErrors).some(error => error)
  }

  const handleMileStoneCreation = async () => {
    if (validateMilestone()) {
      addMilestone(draftMilestone)
    }
  }

  const milestoneTypeOptions = Object.entries(MILESTONE_TO_ICON_MAPPER).map(([key, value]) => ({
    label: '',
    value: key,
    icon: value
  }))

  const resetErrors = () => {
    setErrors({
      milestone_amount: false,
      milestone_text: false,
      milestone_header: false,
      milestone_header_char_count: false,
      milestone_amount_same: false
    })
  }

  return {
    milestoneTypeOptions,
    errors,
    handleMileStoneCreation,
    validateMilestone,
    resetErrors,
    setErrors
  }
}

type AddFileStoneFormProps = {
  milestone: Milestone
  setMilestone: (milestone: Milestone) => void
  milestoneTypeOptions: { label: string; value: string; icon: () => React.ReactNode }[]
  errors: {
    milestone_amount: boolean
    milestone_amount_same: boolean
    milestone_text: boolean
    milestone_header: boolean
    milestone_header_char_count: boolean
  }
  setErrors: (errors: {
    milestone_amount: boolean
    milestone_amount_same: boolean
    milestone_text: boolean
    milestone_header: boolean
    milestone_header_char_count: boolean
  }) => void
}

const AddFileStoneForm = ({
  milestone,
  setMilestone,
  milestoneTypeOptions,
  errors,
  setErrors
}: AddFileStoneFormProps) => {
  const {
    getters: { isCountBased }
  } = useCartContext()
  const { t } = useLocale()

  const headerErrors = useMemo(() => {
    if (errors.milestone_header) {
      return (
        <Typography color='error' variant='caption' sx={{ fontWeight: 500 }}>
          Milestone header must not be empty and must contain only one {`<value>`}
        </Typography>
      )
    } else if (errors.milestone_header_char_count) {
      return (
        <Typography color='error' variant='caption' sx={{ fontWeight: 500 }}>
          Milestone header must be less than 50 characters
        </Typography>
      )
    }
  }, [errors.milestone_header, errors.milestone_header_char_count])

  const amountErrorMessage = useMemo(() => {
    if (errors.milestone_amount) {
      if (isCountBased) {
        return 'Milestone count must be greater than 0'
      } else {
        return 'Milestone amount must be greater than 0'
      }
    } else if (errors.milestone_amount_same) {
      if (isCountBased) {
        return 'Milestone count already exists'
      } else {
        return 'Milestone amount already exists'
      }
    }
  }, [errors.milestone_amount, errors.milestone_amount_same, isCountBased])

  return (
    <form className='space-y-4'>
      <div className='flex flex-col gap-2'>
        <InputTextField
          inputSize='small'
          inputType='number'
          inputValue={milestone.milestone_amount.toString()}
          isFullWidth={false}
          inputClass='w-[200px] text-sm'
          textFieldId='milestone-amount'
          label={
            isCountBased ? 'Count of products required for the milestone' : 'Cart value required for the milestone'
          }
          inputProps={{ maxLength: 15 }}
          startIcon={isCountBased ? <></> : <RupeeIcon />}
          errorStatus={errors.milestone_amount || errors.milestone_amount_same}
          errorMessage={amountErrorMessage}
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setErrors({
              ...errors,
              milestone_amount: false,
              milestone_amount_same: false
            })
            setMilestone({
              ...milestone,
              milestone_amount: Number(e.target.value)
            })
          }}
        />
      </div>
      <div className='flex flex-row gap-2'>
        <div className='mr-12'>
          <Typography variant='body2' sx={{ fontWeight: 500, fontSize: 12 }}>
            {t('milestone_icon_helper_text')}
          </Typography>
          <DropdownButton
            renderOnlyIcon
            variant='outlined'
            options={milestoneTypeOptions}
            handleClick={(index: number, value: string) => {
              setMilestone({
                ...milestone,
                milestone_type: value as Milestone['milestone_type']
              })
            }}
            selectedOption={milestoneTypeOptions.findIndex(option => option.value === milestone.milestone_type)}
            fullWidth
            size='large'
          ></DropdownButton>
        </div>
        <InputTextField
          inputValue={milestone.milestone_text}
          inputClass='w-[200px] text-sm'
          isFullWidth={false}
          textFieldId='milestone-text'
          label={'Offer title on the milestone:'}
          inputProps={{ maxLength: 15 }}
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setErrors({
              ...errors,
              milestone_text: false
            })
            setMilestone({
              ...milestone,
              milestone_text: e.target.value
            })
          }}
          errorStatus={errors.milestone_text}
          errorMessage={errors.milestone_text ? 'Milestone text must not be empty' : ''}
          showCharacterCountConfig={{
            showOnlyOnFocus: false,
            maxLength: 15
          }}
        />
      </div>
      <div className='flex flex-col'>
        <Typography variant='body2' sx={{ fontWeight: 500, fontSize: 12 }}>
          {t('milestone_header_helper_text')}
        </Typography>
        <ValueEditor
          value={milestone.milestone_header.replace(/{{price}}|{{value}}/g, '<value>')}
          onChange={(newValue: string) => {
            setErrors({
              ...errors,
              milestone_header: false,
              milestone_header_char_count: false
            })
            // change the delimiters to {{price}} from <value>
            const newValueWithDelimiters = newValue.replace(/<value>/g, '{{price}}')
            setMilestone({
              ...milestone,
              milestone_header: newValueWithDelimiters
            })
          }}
          error={errors.milestone_header}
          charLimit={50}
        />
        {headerErrors}
        <div className='flex flex-row justify-center items-center gap-1 w-fit p-2 rounded-lg'>
          <Typography variant='body2' sx={{ fontWeight: 400, fontSize: 12, whiteSpace: 'nowrap' }}>
            {isCountBased ? t('milestone_header_example_text_count_based') : t('milestone_header_example_text')}
          </Typography>
          <Value
            onClick={() => {
              navigator.clipboard.writeText(`<value>`)
              successToast('Copied to clipboard')
            }}
          >
            <span className='text-xs'>{`<value>`}</span>
          </Value>
          {isCountBased && (
            <Typography variant='body2' sx={{ fontWeight: 400, fontSize: 12, whiteSpace: 'nowrap' }}>
              {t('milestone_header_example_text_count_based_suffix')}
            </Typography>
          )}
        </div>
        <div className=' bg-[#C7981F1A] flex flex-row justify-center items-center gap-1 w-fit p-2 rounded-lg border-yellow-400 border-1 mt-2'>
          <Info size={16} className='pr-1' color='#C7981F' />
          <Typography variant='body2' sx={{ fontWeight: 400, fontSize: 12, whiteSpace: 'nowrap' }}>
            {t('milestone_header_info_text_first')}
          </Typography>
          <Value
            onClick={() => {
              navigator.clipboard.writeText(`<value>`)
              successToast('Copied to clipboard')
            }}
          >
            <span className='text-xs'>{`<value>`}</span>
          </Value>
          <Typography variant='body2' sx={{ fontWeight: 400, fontSize: 12, whiteSpace: 'nowrap' }}>
            {isCountBased ? t('milestone_header_info_text_second_count_based') : t('milestone_header_info_text_second')}
          </Typography>
        </div>
      </div>
    </form>
  )
}
const AddMilestoneDialog: React.FC<{ isOpen: boolean; setIsOpen: (isOpen: boolean) => void }> = ({
  isOpen,
  setIsOpen
}) => {
  const { t } = useLocale()
  const {
    state: { draftMilestone },
    actions: { setDraftMilestone }
  } = useCartContext()
  const { milestoneTypeOptions, handleMileStoneCreation, errors, resetErrors, setErrors } = useAddMilestone()

  return (
    <FloGenericDialog
      width={530}
      isOpen={isOpen}
      handleClose={() => {
        resetErrors()
        setIsOpen(false)
      }}
      handleJob={handleMileStoneCreation}
      jobButtonText={'Save'}
      closeButtonText={'Cancel'}
      content={
        <AddFileStoneForm
          errors={errors}
          setErrors={setErrors}
          milestone={draftMilestone}
          setMilestone={setDraftMilestone}
          milestoneTypeOptions={milestoneTypeOptions}
        />
      }
      title={t('add_milestone')}
    />
  )
}

export default AddMilestoneDialog
